import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, format } from 'date-fns';

export type DateFilter = 'today' | 'week' | 'month';

interface DashboardStats {
  totalSales: number;
  totalExpenses: number;
  pendingAmount: number;
  profit: number;
}

interface ChartDataPoint {
  name: string;
  sales: number;
  expenses: number;
}

interface CategoryDataPoint {
  name: string;
  value: number;
  color: string;
}

interface RecentTransaction {
  id: string;
  customer: string;
  service: string;
  amount: number;
  mode: string;
  status: string;
  dueAmount: number;
  date: string;
}

const CHART_COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

export function useDashboardStats(filter: DateFilter = 'today') {
  const { tenant } = useAuth();

  const dateRange = useMemo(() => {
    const now = new Date();
    switch (filter) {
      case 'today':
        return { start: startOfDay(now), end: endOfDay(now) };
      case 'week':
        return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
      case 'month':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      default:
        return { start: startOfDay(now), end: endOfDay(now) };
    }
  }, [filter]);

  // Main dashboard stats query with caching
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats', tenant?.id, dateRange.start.toISOString(), dateRange.end.toISOString()],
    queryFn: async () => {
      if (!tenant?.id) return null;
      
      const startStr = dateRange.start.toISOString();
      const endStr = dateRange.end.toISOString();

      // Fetch all stats in parallel - single batch
      const [salesResult, expensesResult, pendingResult, recentResult] = await Promise.all([
        supabase
          .from('sales')
          .select('total_amount')
          .eq('tenant_id', tenant.id)
          .eq('is_deleted', false)
          .gte('created_at', startStr)
          .lte('created_at', endStr),
        
        supabase
          .from('expenses')
          .select('amount')
          .eq('tenant_id', tenant.id)
          .gte('created_at', startStr)
          .lte('created_at', endStr),
        
        supabase
          .from('sales')
          .select('due_amount')
          .eq('tenant_id', tenant.id)
          .eq('is_deleted', false)
          .or('payment_status.eq.unpaid,payment_status.eq.partial'),
        
        supabase
          .from('sales')
          .select(`
            id,
            customer_name,
            total_amount,
            payment_mode,
            payment_status,
            due_amount,
            created_at,
            sale_items (service_name)
          `)
          .eq('tenant_id', tenant.id)
          .eq('is_deleted', false)
          .order('created_at', { ascending: false })
          .limit(5),
      ]);

      const totalSales = salesResult.data?.reduce((sum, s) => sum + (s.total_amount || 0), 0) || 0;
      const totalExpenses = expensesResult.data?.reduce((sum, e) => sum + (e.amount || 0), 0) || 0;
      const pendingAmount = pendingResult.data?.reduce((sum, s) => sum + (s.due_amount || 0), 0) || 0;

      const recentTransactions: RecentTransaction[] = (recentResult.data || []).map((sale: any) => ({
        id: sale.id,
        customer: sale.customer_name || 'Walk-in Customer',
        service: sale.sale_items?.[0]?.service_name || 'Service',
        amount: sale.total_amount || 0,
        mode: sale.payment_mode || 'Cash',
        status: sale.payment_status || 'paid',
        dueAmount: sale.due_amount || 0,
        date: sale.created_at,
      }));

      return {
        stats: {
          totalSales,
          totalExpenses,
          pendingAmount,
          profit: totalSales - totalExpenses,
        },
        recentTransactions,
      };
    },
    enabled: !!tenant?.id,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
  });

  // Chart data query - OPTIMIZED: All 7 days in parallel batch
  const { data: chartData = [], isLoading: chartLoading } = useQuery({
    queryKey: ['dashboard-chart', tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return [];

      const days = [];
      for (let i = 6; i >= 0; i--) {
        const date = subDays(new Date(), i);
        days.push({
          date,
          name: format(date, 'EEE'),
          start: startOfDay(date).toISOString(),
          end: endOfDay(date).toISOString(),
        });
      }

      // OPTIMIZATION: Create all promises first, then execute in parallel
      const salesPromises = days.map(day => 
        supabase
          .from('sales')
          .select('total_amount')
          .eq('tenant_id', tenant.id)
          .eq('is_deleted', false)
          .gte('created_at', day.start)
          .lte('created_at', day.end)
      );

      const expensesPromises = days.map(day => 
        supabase
          .from('expenses')
          .select('amount')
          .eq('tenant_id', tenant.id)
          .gte('created_at', day.start)
          .lte('created_at', day.end)
      );

      // Execute ALL 14 queries in parallel (not sequential!)
      const [salesResults, expensesResults] = await Promise.all([
        Promise.all(salesPromises),
        Promise.all(expensesPromises),
      ]);

      // Map results to chart data
      const chartResults: ChartDataPoint[] = days.map((day, index) => ({
        name: day.name,
        sales: salesResults[index].data?.reduce((sum, s) => sum + (s.total_amount || 0), 0) || 0,
        expenses: expensesResults[index].data?.reduce((sum, e) => sum + (e.amount || 0), 0) || 0,
      }));

      return chartResults;
    },
    enabled: !!tenant?.id,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  // Category data query with caching
  const { data: categoryData = [{ name: 'No Data', value: 0, color: CHART_COLORS[0] }], isLoading: categoryLoading } = useQuery({
    queryKey: ['dashboard-categories', tenant?.id, dateRange.start.toISOString(), dateRange.end.toISOString()],
    queryFn: async () => {
      if (!tenant?.id) return [{ name: 'No Data', value: 0, color: CHART_COLORS[0] }];

      const startStr = dateRange.start.toISOString();
      const endStr = dateRange.end.toISOString();

      const { data: saleItems } = await supabase
        .from('sale_items')
        .select(`
          total_price,
          service_id,
          services (category),
          sales!inner (tenant_id, is_deleted)
        `)
        .gte('created_at', startStr)
        .lte('created_at', endStr)
        .eq('sales.tenant_id', tenant?.id)
        .eq('sales.is_deleted', false);

      const categoryMap = new Map<string, number>();
      
      (saleItems || []).forEach((item) => {
        const category = item.services?.category || 'Other';
        categoryMap.set(category, (categoryMap.get(category) || 0) + (item.total_price || 0));
      });

      const categories: CategoryDataPoint[] = Array.from(categoryMap.entries())
        .map(([name, value], index) => ({
          name,
          value,
          color: CHART_COLORS[index % CHART_COLORS.length],
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 5);

      return categories.length > 0 ? categories : [{ name: 'No Data', value: 0, color: CHART_COLORS[0] }];
    },
    enabled: !!tenant?.id,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const isLoading = statsLoading || chartLoading || categoryLoading;

  return {
    stats: statsData?.stats || {
      totalSales: 0,
      totalExpenses: 0,
      pendingAmount: 0,
      profit: 0,
    },
    chartData,
    categoryData,
    recentTransactions: statsData?.recentTransactions || [],
    isLoading,
  };
}
